import { msg } from "@lingui/core/macro";
import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  Show,
  untrack,
} from "solid-js";
import {
  Outlet,
  useLocation,
  useNavigate,
  useRouterState,
  useSearch,
} from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { CommandPalette } from "@/features/search/components/command-palette";
import { MainContentSkeleton } from "@/components/workspace-skeleton";
import { ShortcutsModal } from "@/components/shortcuts-modal";
import { AppErrorBoundary } from "@/components/error-boundary";

import { useInteractionFlag } from "@fleet/ui";
import { AppLayoutContext } from "../context";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(props: AppLayoutContentProps) {
  const navigate = useNavigate();
  const searchPalette = useSearchPalette();
  const { t } = useI18n();

  // Global overlays addressable by URL (inherited from the `_app` layout):
  //   ?palette=1[&q=&filter=]  → Command Palette (global search)
  //   ?modal=shortcuts         → Shortcuts Modal (help)
  // The URL is the source of truth: pasting the link reopens the exact overlay. It
  // is synchronized bidirectionally with the existing signals (which still
  // govern internal logic and keyboard shortcuts). Idempotent: `replace`
  // does not inflate history and the other search params are preserved.
  const overlaySearch = useSearch({ strict: false }) as () => {
    palette?: boolean;
    modal?: string;
    q?: string;
    scope?: string;
  };

  const handleCloseSearch = () => {
    searchPalette.close();
  };

  const location = useLocation();
  const routerState = useRouterState();

  const isNavigating = () => routerState().status === "pending";

  // SECTION key for the page-entry animation: the `routeId` of the
  // active sheet, but collapsing note detail (`/_app/notes/$pageId`) to one
  // key. Thus, navigating between sibling notes (carousel / tree click)
  // does NOT re-trigger entry (the `$pageId` changes but the section is the same),
  // and the TipTap editor does not remount annoyingly because of the wrapper.
  // Any other route uses its routeId as-is → entry ONCE per section.
  const sectionKey = createMemo(() => {
    const { matches } = routerState();
    const leafId = matches.at(-1)?.routeId ?? "/";
    return leafId.includes("/notes/$pageId") ? "/_app/notes/$pageId" : leafId;
  });

  const theme = useTheme();

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);
  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);

  // Close the mobile sidebar drawer whenever the route changes.
  onMount(() => {
    void location().pathname;
  });

  useAppShortcuts({
    searchPalette,
    location,
    navigate,
    theme,
    t,
    shortcutsOpen,
    setShortcutsOpen,
    setIsSidebarOpen,
  });

  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, it owns horizontal swiping (drag-to-dismiss).
  // Announcing it here — where the signal lives — keeps the flag reusable by
  // any future window-bound gesture without re-deriving it from the DOM.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  // ── URL ⇄ overlay signals (palette / shortcuts) ─────────────────────────────
  const wantsPalette = () => overlaySearch().palette === true;
  const wantsShortcuts = () => overlaySearch().modal === "shortcuts";

  // `to: "."` preserves the current route and only toggles inherited search
  // params from the `_app` layout; the reducer lets TanStack infer the type of `prev`.
  const setPaletteUrl = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        palette: open ? true : undefined,
        // When closing, also clear the search state in the URL.
        ...(open ? {} : { q: undefined, scope: undefined }),
      }),
      replace: true,
    });
  };
  const setShortcutsUrl = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        modal: open ? ("shortcuts" as const) : undefined,
      }),
      replace: true,
    });
  };

  // URL → signals (pasted link, back/forward).
  // NOTE (CC-16): state is read in `untrack` — if the effect also tracks
  // searchPalette.isOpen()/shortcutsOpen(), opening the overlay state-first
  // (⌘K, "?") retriggers it with the OLD search params (navigate is async)
  // and immediately CLOSES it. It should react only to EXTERNAL URL changes.
  createEffect(() => {
    const want = wantsPalette();
    untrack(() => {
      if (want !== searchPalette.isOpen()) {
        if (want) {
          searchPalette.open();
        } else {
          searchPalette.close();
        }
      }
    });
  });
  createEffect(() => {
    const want = wantsShortcuts();
    if (want !== untrack(shortcutsOpen)) {
      setShortcutsOpen(want);
    }
  });

  // signals → URL (atajos de teclado, botones de cabecera).
  createEffect(() => {
    if (searchPalette.isOpen() !== wantsPalette()) {
      setPaletteUrl(searchPalette.isOpen());
    }
  });
  createEffect(() => {
    if (shortcutsOpen() !== wantsShortcuts()) {
      setShortcutsUrl(shortcutsOpen());
    }
  });

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
      glow0={theme.tokens()?.glow0}
      glow1={theme.tokens()?.glow1}
      glow2={theme.tokens()?.glow2}
    >
      {/* Skip-to-content: first tabbable, hidden until it receives focus (a11y). */}
      <a
        href="#main-content"
        class="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[200] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent"
      >
        {t(msg`Skip to content`)}
      </a>

      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

      {/* ── Main content ───────────────────────────────────────── */}
      <main
        id="main-content"
        tabindex="-1"
        class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)] focus:outline-none"
      >
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          <AppLayoutContext.Provider value={{}}>
            <div class="flex flex-1 flex-col min-h-0 h-full">
              {/* The Outlet's routes read PowerSync themselves; until the
                  local DB is ready there is no data to render, so show a
                  granular content skeleton in this region only. */}
              {/* ErrorBoundary for the main data region: isolates a failure
                  de cualquier ruta hija (editor, árbol…) del shell — el header,
                  sidebar y navegación siguen vivos para reintentar/recargar. */}
              <AppErrorBoundary variant="page" label="main-content">
                <Show when={props.db} fallback={<MainContentSkeleton />}>
                  {/* Page enter animation, ONCE per route SECTION
                      ruta (canon "estilo perfil"). `keyed` por `sectionKey()`
                      remonta este wrapper al cambiar de sección, re-disparando
                      `slide-in-up`; las sub-rutas (p. ej. notas hermanas)
                      colapsan a la misma clave y NO re-animan. Este div es
                      SEPARADO del que recibe el `transform` del carrusel (ese
                      vive dentro de NotePageView), así no pelean. `will-change`
                      se limpia al terminar para no fijar capas GPU. */}
                  <Show when={sectionKey()} keyed>
                    {(_section) => (
                      <div
                        class="flex flex-1 flex-col min-h-0 h-full motion-safe:animate-slide-in-up motion-safe:will-change-[transform,opacity]"
                        on:animationend={(event) => {
                          if (event.target === event.currentTarget) {
                            event.currentTarget.style.willChange = "auto";
                          }
                        }}
                      >
                        <Outlet />
                      </div>
                    )}
                  </Show>
                </Show>
              </AppErrorBoundary>
            </div>
          </AppLayoutContext.Provider>
        </div>
      </main>

      <ShortcutsModal
        isOpen={shortcutsOpen()}
        onClose={() => {
          setShortcutsOpen(false);
        }}
      />

      {/* Command Palette — rendered last to avoid blur compositor flash */}
      <CommandPalette isOpen={searchPalette.isOpen()} onClose={handleCloseSearch} />
    </AppBackground>
  );
}
